/**
 * Paths the React frontend links to directly.
 *
 * Rails stays the source of truth for routing; `test/frontend_routes_test.rb` asserts
 * every entry here against the matching Rails route helper, so a renamed route fails the
 * Ruby suite instead of silently 404ing in the browser. Add a route in this file and in
 * that test together.
 */
export const ROUTES = {
  home: '/',
  /** The tools landing page: "New Test" in the navigation. */
  tools: '/tools/new-test',
  urlGenerator: '/tools/url-generator',
  collections: '/tools/collections',
  /**
   * The manifest inspector: fetch an .m3u8 or .mpd and read it.
   *
   * The child path is the drill-down - opening one variant of a master playlist - and it is a
   * second route rather than a query parameter because it needs a `parent_url` to resolve a
   * relative uri against, and a GET would put both in the browser's history where the next
   * request could not distinguish them.
   */
  manifestInspector: '/tools/manifest-inspector',
  manifestInspectorChild: '/tools/manifest-inspector/child',
  httpInspector: '/tools/http-inspector',
  /**
   * Testing Results, behind the rail: the list with its filters and its search, for the
   * visitor who wants to find something in it.
   */
  history: '/tools/logs',
  /**
   * Activity, in the site header before Tools: the public list of recent tests and the
   * detail its rows open.
   *
   * This is the url that was a placeholder page while this one was built; the placeholder
   * is deleted. Not a redirect to `history` and not a second copy of it either: the same
   * public streams for everybody, no search, and its own views — see `ActivityController`.
   */
  activity: '/logs',
  login: '/users/login',
  /**
   * The sign-up *page*, at /users/register. `register` below is where the form *posts* -
   * different paths, and writing the page's path into a POST 404s. Added for the stream
   * tester's "Register to run a private test" link, which a guest reads before a run.
   */
  signUp: '/users/register',
  logout: '/users/logout',
  /**
   * Where a sign-up form *posts*, which is not where the form lives. `/users/register` is the
   * page; `/users` is the collection. They are different paths and writing the form's own
   * path into a POST is a 404 - one that looks like a pass to `assert_no_difference`,
   * because the account really is not created.
   */
  register: '/users',
  /** The account page. Linked from the rail, so it is the member's working page. */
  account: '/users/edit',
  /** The forgot-password *form*; the page itself is at forgotPassword below. */
  sendResetPassword: '/users/secret',
  /**
   * The reset-password page, with the token in the query: `/users/secret/edit?reset_password_token=…`.
   *
   * `useForm` does the request itself and so there is nothing in the DOM to read this from -
   * a client-side page cannot read the query off history state either. The token arrives as a
   * prop from the controller instead; see pages/auth/reset-password.tsx.
   */
  resetPassword: '/users/secret',
  /** The resend-confirmation form; the page itself is at resendConfirmationPage below. */
  sendConfirmation: '/users/verification',
  privacy: '/privacy',
  about: '/about',
  changelog: '/changelog',
  contactUs: '/contact-us',
  /** Where the /tools challenge form posts; redirect-after-post back to the page asked for. */
  toolsVerify: '/tools/verify',
} as const

export type ShellUser = {
  id: number
  email: string
  nickname: string | null
} | null
